import React, { useState, useEffect, useRef } from 'react';
import { Box, Modal } from 'native-base';
import { invokeSupabaseFunction } from '../lib/supabase';
import { Image } from 'expo-image';
import { ImageBackground, View, TouchableOpacity, Text, KeyboardAvoidingView, Platform, SafeAreaView, Dimensions, TextInput, NativeSyntheticEvent, TextInputContentSizeChangeEventData } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { GiftedChat, IMessage, MessageImageProps, MessageVideoProps } from 'react-native-gifted-chat';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import FontAwesome6 from '@expo/vector-icons/FontAwesome6';
import { RootStackParamList } from '../navigation';
import { db, ChatMessage } from '../data/database';
import { SEED_MESSAGES } from '../data/seed-messages';
import { MediaModal } from '../components/chat/mediaModal';
import ChatFooter from '../components/chat/chatFooter';
import ChatHeader from '../components/chat/chatHeader';
import { CurrentMsgMedias } from '../data/types';
import { ChatsAPI } from '../data/api';
import PhoneScreen from './PhoneScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'Message'>;

const TypewriterText = ({
  text, isComplete, contentId
}: {
  text: string, isComplete: boolean, contentId: number | string
}) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Split text into array of characters for Chinese and words for English
  const tokens = text.split('').reduce((acc: string[], char) => {
    if (/[\u4e00-\u9fa5]/.test(char)) {
      // Chinese character
      acc.push(char);
    } else if (char === ' ') {
      // Space character
      if (acc.length && acc[acc.length - 1] !== ' ') {
        acc.push(char);
      }
    } else {
      // English or other characters
      if (acc.length && !/[\u4e00-\u9fa5]/.test(acc[acc.length - 1]) && acc[acc.length - 1] !== ' ') {
        acc[acc.length - 1] += char;
      } else {
        acc.push(char);
      }
    }
    return acc;
  }, []);
  
  useEffect(() => {
    if (!isComplete && currentIndex < tokens.length) {
      const timer = setTimeout(() => {
        setDisplayText(prev => prev + tokens[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, 100); // Faster delay for smoother animation
      
      return () => clearTimeout(timer);
    }
  }, [currentIndex, tokens, isComplete]);

  // Show full text immediately if message is complete
  useEffect(() => {
    if (isComplete) {
      setDisplayText(text);
      setCurrentIndex(tokens.length);
    }
  }, [isComplete, text, tokens.length])

  return <Text style={{ color: contentId === 1 ? '#FFF' : '#000', fontFamily: Platform.OS === 'ios' ? 'System' : 'Roboto' }}>{displayText}</Text>;
};

export const MessageScreen = ({ navigation, route }: Props) => {
  const { character } = route.params || {};
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [currentMessageMedias, setCurrentMessageMedias] = useState<CurrentMsgMedias[]>([]);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const completeId = useRef<number | string>("");
  
  const formatMessageWithImages = (msg: ChatMessage, userName: string) => {
    const result: IMessage[] = [];
    
    // Skip system messages in the UI
    if (msg.isSystem) return result;

    const mediaUrls = msg.content.match(/https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|mp4)(?=[\s"']|$)/gi) || [];
    const textContent = msg.content.replace(/https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|mp4)(?=[\s"']|$)/gi, '').trim();
    
    // Create base message with text content
    if (textContent) {
      result.push({
        _id: msg.id,
        text: textContent,
        createdAt: new Date(msg.timestamp),
        user: {
          _id: msg.isUser ? 1 : 2,
          name: userName
        }
      });
    }
    
    // Create separate message for each image
    mediaUrls.forEach((url, index) => {
      result.push({
        _id: `${msg.id}_image_${index}`,
        text: '',
        createdAt: new Date(msg.timestamp + index), // Slightly offset timestamps to maintain order
        user: {
          _id: msg.isUser ? 1 : 2,
          name: userName
        },
        image: url
      });
    });
    
    return result;
  };

  useEffect(() => {
    if (character) {
      initializeChat();
    }
  }, [character]);

  const initializeChat = async () => {
    if (!character) return;
    const sp_chats = await ChatsAPI.getHistoryList(1, 20, character.id) || [];
    let chats = sp_chats.map(item=>item.content);

    if (!chats.length) {
      // Load seed messages for new chats
      const seedMessages = SEED_MESSAGES[character.id] || [];
      let api_list: typeof chats = [];
      for (const seedMessage of seedMessages) {
        if(seedMessage.content === "Image"){
          seedMessage.content = character.albums!.closeup[0]
        }
        chats.push(seedMessage);
        api_list.push(seedMessage);
        // await db.saveChatMessage(chat.id, seedMessage);
      }
      ChatsAPI.saveHistoryCtxs(api_list, character.id);
    };
    // Add system message with character's background story
    if (character.backgroundStory) {
      const systemMessage: ChatMessage = {
        id: `system_${Date.now()}`,
        characterId: character.id,
        content: character.backgroundStory,
        timestamp: Date.now(),
        isUser: false,
        isSystem: true
      };
      chats.push(systemMessage);
      // await db.saveChatMessage(chat.id, systemMessage);
    };
    
    // setChatId(chat.id);
    // const chatMessages = await db.getChatMessages(chat.id);
    const chatMessages = chats;
    const formattedMessages: IMessage[] = [];
    
    chatMessages.forEach(msg => {
      const userName = msg.isUser ? 'User' : character?.name || 'Assistant';
      formattedMessages.push(...formatMessageWithImages(msg, userName));
    });
    // formattedMessages[0].video = "http://viralsauce.ai/download/imported_tk_videos/7471230350182731039.mp4";
    
    setMessages( formattedMessages );
  };

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const onSend = async (newMessages: IMessage[] = []) => {
    if (!character) return;
    const userMessage = newMessages[0];
    setLoading(true);

    const newChatMessage: ChatMessage = {
      id: Date.now().toString(),
      characterId: character.id,
      content: userMessage.text,
      timestamp: Date.now(),
      isUser: true
    };
    ChatsAPI.saveHistoryCtxs([newChatMessage], character.id);
    // await db.saveChatMessage(chatId, newChatMessage);

    // Prepare the AI response message
    const aiMessageId = (Date.now() + 1).toString();
    // Add user message to the UI immediately
    let contentMsg = [...messages];
  
    try {
      contentMsg.unshift({
        _id: aiMessageId,
        text: '...',
        createdAt: new Date(),
        user: { _id: 2, name: character.name }
      }, {
        _id: newChatMessage.id,
        text: newChatMessage.content,
        createdAt: new Date(newChatMessage.timestamp),
        user: { _id: 1, name: 'User' }
      });
      setMessages(contentMsg);
  
      // Format the conversation history
      const conversationHistory = [];

      // Add background story as system message if available
      if (character.backgroundStory) {
        conversationHistory.push({
          role: 'system',
          content: character.backgroundStory
        });
      }

      // Add message history
      conversationHistory.push(...messages.slice(0, 10).reverse().map(msg => ({
        role: msg.user._id === 1 ? 'user' : 'assistant',
        content: msg.text
      })));

      // Add the current message
      conversationHistory.push({
        role: 'user',
        content: userMessage.text
      });
      
      // Call OpenAI API with streaming enabled
      const { data: response, error: functionError } = await invokeSupabaseFunction('openai-proxy', {
        messages: conversationHistory,
        stream: true,
        model: 'gpt-3.5-turbo'
      });

      if (functionError) {
        throw functionError;
      }
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const reader = response.body?.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let aiMessageContent = '';

      while (reader) {
        const { value, done } = await reader.read();
        if (done) break;
        console.log(value);
  
        // Decode the chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete lines from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep the last incomplete line in buffer
  
        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || trimmedLine.includes('[DONE]')) continue;
          if (!trimmedLine.startsWith('data: ')) continue;
  
          try {
            const jsonStr = trimmedLine.slice(6);
            const data = JSON.parse(jsonStr);
            if (!data?.choices?.[0]?.delta?.content) continue;
  
            aiMessageContent += data.choices[0].delta.content;

            const updatedMessages = [...contentMsg];
            const aiMessageIndex = updatedMessages.findIndex(msg => msg._id === aiMessageId);
            if (aiMessageIndex !== -1) {
              const formattedMessages = formatMessageWithImages({
                id: aiMessageId,
                characterId: character.id,
                content: aiMessageContent,
                timestamp: Date.now(),
                isUser: false
              }, character.name);
              // Replace the temporary message with formatted messages
              updatedMessages.splice(aiMessageIndex, 1, ...formattedMessages);
              completeId.current = formattedMessages[0]._id;
            };
            // Update the AI message in real-time
            setMessages(updatedMessages);
          } catch (e) {
            console.error('Error parsing streaming response:', e);
            // Continue to next line if parsing fails
            continue;
          }
        }
      }
  
      // Save the complete AI response to the database
      // Check if AI mentioned "picture" and has available albums
      let finalContent = aiMessageContent;
      
      // Format the function call to determine image intention
      const functionCall = {
        messages: [{
          role: "user",
          content: aiMessageContent
        }],
        functions: [{
          name: "should_send_image",
          description: "Determine if the message indicates intention to share an image",
          parameters: {
            type: "object",
            properties: {
              should_send: {
                type: "boolean",
                description: "Whether the message indicates intention to share an image"
              }
            },
            required: ["should_send"]
          }
        }],
        function_call: { name: "should_send_image" }
      };

      // Call OpenAI to determine image intention
      const { data: intentionResponse, error: intentionError } = await invokeSupabaseFunction('openai-proxy', functionCall);
      
      if (intentionError) {
        console.error('Error determining image intention:', intentionError);
        return;
      }

      const intentionResult = await intentionResponse.json();
      const shouldSendImage = intentionResult?.choices?.[0]?.message?.function_call?.arguments
        ? JSON.parse(intentionResult.choices[0].message.function_call.arguments).should_send
        : false;
      if (shouldSendImage && character.albums) {
        // Get all available albums and their images
        const allImages = Object.values(character.albums).flat();
        if (allImages.length > 0) {
          const nextImageUrl = allImages[currentImageIndex];
          finalContent = `${aiMessageContent}\n${nextImageUrl}`;
          setCurrentImageIndex((prevIndex) => (prevIndex + 1) % allImages.length);
          
          const updatedMessages = [...contentMsg];
          const aiMessageIndex = updatedMessages.findIndex(msg => msg._id === aiMessageId);
          if (aiMessageIndex !== -1) {
            const formattedMessages = formatMessageWithImages({
              id: aiMessageId,
              characterId: character.id,
              content: finalContent,
              timestamp: Date.now(),
              isUser: false
            }, character.name);
            
            // Replace the temporary message with formatted messages
            updatedMessages.splice(aiMessageIndex, 1, ...formattedMessages);
          }
          // Update messages with properly formatted content including image
          setMessages(updatedMessages);
        }
      }

      const aiResponse: ChatMessage = {
        id: aiMessageId,
        characterId: character.id,
        content: finalContent,
        timestamp: Date.now(),
        isUser: false
      };
  
      // await db.saveChatMessage(chatId, aiResponse);
      ChatsAPI.saveHistoryCtxs([aiResponse], character.id);
  
    } catch (error) {
      console.error('Error in chat:', error);
      // Show error message in chat
      const updatedMessages = [...contentMsg];
      const aiMessageIndex = updatedMessages.findIndex(msg => msg._id === aiMessageId);
      if (aiMessageIndex !== -1) {
        const formattedMessage = {
          _id: Date.now().toString(),
          text: 'Sorry, there was an error processing your message. Please try again.',
          createdAt: new Date(),
          user: { _id: 2, name: character.name }
        };
        completeId.current = formattedMessage._id;
        // Replace the temporary message with formatted messages
        updatedMessages.splice(aiMessageIndex, 1, formattedMessage);
      }
      // Update the AI message in real-time
      setMessages(updatedMessages);
    } finally {
      setLoading(false);
    }
  };

  const viewAllChange = ()=>{
    let {routes} = navigation.getState();
    let end_obj = routes[routes.length - 2];
    if(end_obj && end_obj.name === "Detail"){
      navigation.goBack();
    }else{
      navigation.navigate('Detail',{ character });
    };
  }

  const handleImagePress = (medias: {video?: string; image: string}[], index: number) => {
    setCurrentMessageMedias(medias);
    setSelectedImageIndex(index);
    setImageViewerVisible(true);
  };

  const renderMessageMedia = (props: MessageImageProps<IMessage> | MessageVideoProps<IMessage>) => {
    const message = props.currentMessage;
    const medias = message.image ? [{video: message.video, image: message.image}] : [];
    
    if (medias.length === 0) return null;
  
    return (
      <Box width="100%" maxW="250px" overflow="hidden" borderRadius="lg" mb={1}>
        <View style={{
          flexDirection: 'row', flexWrap: 'wrap', gap: 2, padding: 2, width: '100%'
        }}>
          {medias.map((item, index) => (
            <TouchableOpacity
              key={index}
              onPress={() => handleImagePress(medias, index)}
              style={{
                width: medias.length === 1 ? '100%' : '48%',
                aspectRatio: 9/16, borderRadius: 8, overflow: 'hidden'
              }}>
              <View style={{ width: '100%', height: '100%', position: 'relative'}}>
                <Image
                  source={{ uri: item.image }}
                  style={{
                    flex: 1,
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0
                  }}
                  contentFit="cover"
                  transition={200}
                />
                {item.video && (
                  <View style={{
                    position: "absolute", top: 0, left: 0,
                    width: "100%", height: "100%",
                    justifyContent: "center", alignItems: "center",
                  }}>
                    <FontAwesome6 name="circle-play" size={60} color="#FFFFFF9A" />
                  </View>
                )}
              </View>
            </TouchableOpacity>
          ))}
        </View>
      </Box>
    );
  };
  
  return (
    <Box flex={1} safeAreaBottom>
      <ChatHeader character={character} handleImagePress={handleImagePress}
        prveRoute={(route)=>{
          if(route){
            navigation.navigate("MainTabs", { screen: "Gem"});
          }else{
            navigation.goBack()
          }
        }}
        viewAllChange={ viewAllChange }
      />
      <ImageBackground 
        source={{ uri: character?.imageUrl }} 
        style={{ flex: 1, width: '100%' }}
        resizeMode="cover"
      >
        <View style={{ flex: 1, backgroundColor: 'rgba(0, 0, 0, 0.3)' }}>
          <GiftedChat
            messages={messages}
            user={{ _id: 1 }}
            renderAvatar={null}
            renderMessageImage={renderMessageMedia}
            renderMessageVideo={renderMessageMedia}
            renderBubble={(props) => {
              const isImageMessage = props.currentMessage.image || (props.currentMessage.images && props.currentMessage.images.length > 0);
              const isVideoMessage = props.currentMessage.video;
              const isComplete = completeId.current === props.currentMessage._id;

              return (
                <>
                  <View
                    style={{
                      backgroundColor: props.position === 'right' ? 'rgba(0, 122, 255, 0.7)' : 'rgba(229, 229, 234, 0.7)',
                      padding: isImageMessage ? 0 : 10,
                      borderRadius: 15,
                      maxWidth: '80%',
                      marginVertical: 5,
                    }}
                  >
                    {props.currentMessage.text ? (
                      <TypewriterText 
                        text={props.currentMessage.text}
                        isComplete={!isComplete}
                        contentId={ props.currentMessage.user._id }
                      />
                    ) : null}
                  </View>
                  {
                    isVideoMessage ?
                    props.renderMessageVideo!(props) :
                    isImageMessage ? props.renderMessageImage!(props) :
                    <></>
                  }
                </>
              );
            }}

            renderInputToolbar={(props) => {
              return (
                <ChatFooter onSendChange={onSend}
                  onPlayPhone={()=>{
                    setPhoneOpen(true);
                  }} />
              )
            }}
          />
        </View>
      </ImageBackground>
      <Modal isOpen={phoneOpen} size="full"
        justifyContent="center" alignItems="center"
        style={{
          width: "100%", maxWidth: 460,
          marginLeft: "auto", marginRight: "auto",
        }}>
        {phoneOpen && (
          <PhoneScreen character={character}
            onHangUp={()=> setPhoneOpen(false) } />
        )}
      </Modal>

      <MediaModal 
        open={imageViewerVisible}
        setOpen={setImageViewerVisible}
        list={currentMessageMedias}
        defaultIndex={selectedImageIndex}
      />
    </Box>
  );
};

export default MessageScreen;